import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Sparkles, BookOpen, Brain, Zap, Crown } from 'lucide-react';
export function PSIProDifferenceSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-4762">
      {/* Mystical background effects - matching PSIGatheringSection */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-4763" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse-slow" data-id="element-4764" />
      <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-4765" />

      <motion.div ref={ref} initial={{
      opacity: 0,
      y: 30
    }} animate={inView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.8
    }} className="relative z-10 max-w-4xl mx-auto" data-id="element-4766">
        {/* Decorative header */}
        <div className="flex items-center justify-center gap-4 mb-12" data-id="element-4767">
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-amber-500/50" data-id="element-4768" />
          <Sparkles className="w-8 h-8 text-amber-500" data-id="element-4769" />
          <div className="w-24 h-px bg-gradient-to-l from-transparent via-amber-500/50 to-amber-500/50" data-id="element-4770" />
        </div>

        {/* Main headline */}
        <h2 className="text-2xl sm:text-4xl md:text-6xl font-serif text-center mb-8 sm:mb-12 leading-tight text-stone-100" style={{
        fontFamily: 'Cinzel, serif',
        textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
      }} data-id="element-4771">
          But Here's What Makes{' '}
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent" style={{
          textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
        }} data-id="element-4772">
            PSI PRO
          </span>{' '}
          Different From Just "Getting A Course":
        </h2>

        {/* Body content */}
        <div className="space-y-6 text-lg md:text-xl leading-relaxed text-stone-300" data-id="element-4773">
          <p className="text-2xl font-bold text-stone-100" data-id="element-4774">Look.</p>

          <p data-id="element-4775">
            You could buy{' '}
            <span className="font-bold text-amber-400" data-id="element-4776">
              Peptide Mastery 2.0
            </span>{' '}
            as a standalone product.
          </p>

          <p className="text-stone-400 italic pl-6 border-l-2 border-stone-700" data-id="element-4777">
            (It's normally $2,500. Pay-in-full only. No payment plans.)
          </p>

          <p className="font-semibold text-stone-100" data-id="element-4778">
            And it would be worth every penny.
          </p>

          {/* Feature list */}
          <div className="bg-gradient-to-br from-amber-950/30 to-black border-l-4 border-amber-500 p-4 sm:p-6 md:p-8 my-4 sm:my-6 md:my-8 space-y-3" data-id="element-4779">
            <p className="flex items-start gap-3" data-id="element-4780">
              <span className="text-amber-400 flex-shrink-0" data-id="element-4781">✓</span>
              <span className="text-stone-100" data-id="element-4782">130+ peptides decoded.</span>
            </p>
            <p className="flex items-start gap-3" data-id="element-4783">
              <span className="text-amber-400 flex-shrink-0" data-id="element-4784">✓</span>
              <span className="text-stone-100" data-id="element-4785">54 proven protocols.</span>
            </p>
            <p className="flex items-start gap-3" data-id="element-4786">
              <span className="text-amber-400 flex-shrink-0" data-id="element-4787">✓</span>
              <span className="text-stone-100" data-id="element-4788">
                1,193 scientific citations.
              </span>
            </p>
            <p className="flex items-start gap-3" data-id="element-4789">
              <span className="text-amber-400 flex-shrink-0" data-id="element-4790">✓</span>
              <span className="text-stone-100" data-id="element-4791">Mass spec verification.</span>
            </p>
            <p className="flex items-start gap-3" data-id="element-4792">
              <span className="text-amber-400 flex-shrink-0" data-id="element-4793">✓</span>
              <span className="text-stone-100" data-id="element-4794">Lifetime updates.</span>
            </p>
          </div>

          <p className="text-xl font-bold text-center text-amber-500" style={{
          textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
        }} data-id="element-4795">
            It's the most comprehensive peptide education on the planet.
          </p>

          {/* Transition */}
          <div className="py-8" data-id="element-4796">
            <p className="text-2xl font-bold text-stone-100" data-id="element-4797">
              But here's the thing...
            </p>
          </div>

          <p className="text-xl font-bold text-stone-100 border-l-4 border-purple-500 pl-6 py-2 bg-gradient-to-r from-purple-950/30 to-transparent" data-id="element-4798">
            Having the information is only HALF the battle.
          </p>

          <p className="text-xl font-bold text-stone-100" data-id="element-4799">The other half?</p>

          {/* The paradigm shift section */}
          <div className="space-y-4 pl-6 border-l-2 border-cyan-500/30" data-id="element-4800">
            <p className="text-cyan-400 font-bold" data-id="element-4801">
              Making the paradigm shift.
            </p>
            <p data-id="element-4802">Understanding the PRINCIPLES behind the protocols.</p>
            <p data-id="element-4803">
              Knowing <span className="font-bold text-stone-100" data-id="element-4804">HOW</span> to
              think about biology at the level Brenden and Ali do.
            </p>
            <p data-id="element-4805">Learning to see what mainstream practitioners miss.</p>
            <p data-id="element-4806">
              Developing the ability to design your{' '}
              <span className="font-bold text-stone-100 uppercase" data-id="element-4807">OWN</span>{' '}
              protocols for{' '}
              <span className="font-bold text-stone-100 uppercase" data-id="element-4808">YOUR</span>{' '}
              specific situation.
            </p>
            <p data-id="element-4809">
              Operating with the kind of certainty that comes from{' '}
              <span className="font-bold text-purple-400" data-id="element-4810">MASTERY</span> instead
              of just following templates.
            </p>
          </div>

          {/* Final emphasis */}
          <div className="bg-gradient-to-br from-purple-950/40 via-blue-950/30 to-black border-2 border-purple-500/50 p-4 sm:p-6 md:p-10 my-6 sm:my-8 md:my-12 relative" data-id="element-4811">
            <div className="absolute top-0 left-0 w-10 h-10 border-l-2 border-t-2 border-purple-500" data-id="element-4812" />
            <div className="absolute top-0 right-0 w-10 h-10 border-r-2 border-t-2 border-blue-500" data-id="element-4813" />
            <div className="absolute bottom-0 left-0 w-10 h-10 border-l-2 border-b-2 border-blue-500" data-id="element-4814" />
            <div className="absolute bottom-0 right-0 w-10 h-10 border-r-2 border-b-2 border-purple-500" data-id="element-4815" />

            <p className="text-2xl md:text-3xl font-bold text-center leading-relaxed" style={{
            textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
          }} data-id="element-4816">
              <span className="text-stone-100" data-id="element-4817">And </span>
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent" data-id="element-4818">
                THAT'S
              </span>
              <span className="text-stone-100" data-id="element-4819">
                {' '}
                what you get when you join{' '}
              </span>
              <span className="text-purple-400" data-id="element-4820">PSI PRO.</span>
            </p>
          </div>

          {/* CTA Button */}
          <div className="mt-12 sm:mt-16 text-center" data-id="element-4821">
            <button onClick={() => {
            const pricingSection = document.querySelector('[data-pricing-section]');
            if (pricingSection) {
              pricingSection.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          }} className="inline-block px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black font-bold text-lg sm:text-xl rounded-lg shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_50px_rgba(217,119,6,0.6)] hover:scale-105 transition-all duration-300 min-h-[44px]" data-id="element-4822">
              See How To Join PSI PRO
            </button>
          </div>
        </div>
      </motion.div>
    </section>;
}